import {
  GetMultipleChoiceAssessmentsRequest,
  useGetMultipleChoiceAssessmentMetrics,
  useGetMultipleChoiceAssessments,
} from './api/useMultipleChoiceAssessments';

/**
 * Wide enough to hold every assessment there will ever be: the list is bounded by the
 * sort, not by dates. Fixed rather than "now" so that the query key never drifts and
 * an assessment released while the PWA is open is not filtered out until it reloads.
 */
const ASSESSMENT_TIME_RANGE = {
  fromTime: new Date('2010-01-01'),
  toTime: new Date('2100-01-01'),
};

/**
 * The most recently released assessment of a subject matching `params`, together with
 * its metrics and the reader's own mark for it.
 */
function useLatestAssessment(
  subjectId: string,
  params: GetMultipleChoiceAssessmentsRequest['params'],
) {
  const { data: response, isLoading } = useGetMultipleChoiceAssessments({
    subjectId,
    pageNumber: 1,
    pageSize: 1,
    ...ASSESSMENT_TIME_RANGE,
    sort: '-releaseDate',
    params,
  });

  const assessment = response?.items[0];

  const { data: metrics, isLoading: isMetricsLoading } =
    useGetMultipleChoiceAssessmentMetrics(
      { subjectId, assessmentId: assessment?.id ?? '' },
      assessment !== undefined,
    );

  // The board ranks the earliest sitting that counts and reads its figures off that
  // one, so the mark beside it reads the same sitting — one card quoting two marks
  // for the same simulacro is the reader's problem, not a detail.
  //
  // Read off the metrics alone, never off the list payload's own attempts: a sitting
  // the board does not carry is a sitting the card cannot place, and a mark with no
  // board under it says less than the invitation to sit the simulacro does. The card
  // therefore reads as unsat until the metrics carry the attempt.
  const ownGrade = metrics?.ownScore?.grade;

  return { assessment, metrics, ownGrade, isLoading, isMetricsLoading };
}

export { useLatestAssessment };
